<script lang="ts">
	import Chat from '$lib/Chat.svelte';
	import DocumentsList from '$lib/DocumentsList.svelte';
	import RightPanel from '$lib/RightPanel.svelte';
	import { setWindowContext, WindowContext } from '@astlai/common/contexts/window.context.svelte';
	import { DEFAULT_FALLBACK_MODEL } from 'src/constants/models';
	import { ChatContext, setChatContext } from 'src/contexts/chat.context.svelte';
	import { DocumentContext, setDocumentContext } from 'src/contexts/documents.context.svelte';
	import { LayoutContext, setLayoutContext } from 'src/contexts/layout.context.svelte';
	import { ModalContext, setModalContext } from 'src/contexts/modal.context.svelte';
	import { RightPanelContext, setRightPanelContext } from 'src/contexts/right-panel.context.svelte';
	import { setTenantContext, TenantContext } from 'src/contexts/tenant.context.svelte';
	import { PbService } from 'src/services/pb-service.svelte';
	import { ChatMessage } from 'src/types/llm';
	import { onMount } from 'svelte';

	interface Props {
		apiKey: string;
		tenantId?: string;
		baseUrl?: string;
		model?: string;
		embeddingsVendor?: 'jina' | 'scw';
		initialMessage?: string;
		chatPlaceholder?: string;
	}

	let {
		apiKey,
		tenantId,
		baseUrl = 'https://api.botbakery.app',
		model,
		embeddingsVendor = 'jina',
		initialMessage = `Hallo, wie kann ich dir helfen?`,
		chatPlaceholder
	}: Props = $props();

	// Initialize standalone contexts
	const chatContext = new ChatContext(baseUrl, apiKey, {
		messages: [],
		model,
		sources: [],
		tenant: tenantId,
		embeddingsVendor,
		title: 'Standalone Conversation',
		collectionId: 'pbc_3709231855',
		collectionName: 'conversation',
		id: null
	});
	chatContext.standalone = true;
	setChatContext(chatContext);

	$effect(() => {
		chatContext.chatPlaceholder = chatPlaceholder;
	});

	setLayoutContext(new LayoutContext());
	const rightPanelCtx = setRightPanelContext(new RightPanelContext());
	const docContext = setDocumentContext(new DocumentContext());
	setModalContext(new ModalContext());
	setWindowContext(new WindowContext());
	const tenantContext = new TenantContext();
	setTenantContext(tenantContext);

	onMount(async () => {
		const pbService = new PbService(baseUrl, apiKey);
		if (tenantId) {
			const tenant = await pbService.getTenant(tenantId);
			tenantContext.tenant = tenant;
			chatContext.conversation.model = model || tenant.default_model || DEFAULT_FALLBACK_MODEL;
			chatContext.appendMessage(
				new ChatMessage('assistant', tenant.welcome_message || initialMessage)
			);
		}
		console.log('Standalone chat loaded!');
	});

	// Sync derived document data into document context
	$effect(() => {
		docContext.documents = chatContext.quotedDocuments
			.map((s) => chatContext.documentMap.get(s.doc_id))
			.filter((doc) => doc !== undefined);
		docContext.badges = Object.fromEntries(
			chatContext.quotedDocuments.map((s) => [
				s.doc_id,
				{ color: s.citation_color, index: s.citation_index }
			])
		);
		docContext.quotes = Object.fromEntries(
			chatContext.quotedDocuments.map((s) => [s.doc_id, s.inlineQuotes])
		);
		docContext.webSources = chatContext.quotedWebSources;
	});

	// Open the panel when a source is selected
	$effect(() => {
		if (docContext.selected != null && !rightPanelCtx.current) {
			rightPanelCtx.open(DocumentsList);
		}
	});
</script>

<div class="standalone-chat">
	<div class="chat-area">
		<Chat />
	</div>
	<RightPanel />
</div>

<style lang="scss">
	.standalone-chat {
		display: flex;
		flex-direction: row;
		height: 100%;
		width: 100%;
		overflow: hidden;
		background: var(--surface-1, #ffffff);
		position: relative;
	}

	.chat-area {
		flex: 1 1 auto;
		display: flex;
		flex-direction: column;
		overflow: hidden;
	}
</style>
